import { z } from 'zod';
import { PaginationOffsetSchema } from './shared/pagination.js';

// ============================================================================
// Admin Apify - Common types
// ============================================================================

export const ApifyRunStatusSchema = z.enum([
  'READY',
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
  'TIMING-OUT',
  'TIMED-OUT',
  'ABORTING',
  'ABORTED',
]);

export const ApifyRunStatsSchema = z.object({
  inputBodyLen: z.number().optional(),
  restartCount: z.number().optional(),
  resurrectCount: z.number().optional(),
  memAvgBytes: z.number().optional(),
  memMaxBytes: z.number().optional(),
  memCurrentBytes: z.number().optional(),
  cpuAvgUsage: z.number().optional(),
  cpuMaxUsage: z.number().optional(),
  cpuCurrentUsage: z.number().optional(),
  netRxBytes: z.number().optional(),
  netTxBytes: z.number().optional(),
  durationMillis: z.number().optional(),
  runTimeSecs: z.number().optional(),
  metamorph: z.number().optional(),
  computeUnits: z.number().optional(),
});

export const ApifyRunOptionsSchema = z.object({
  build: z.string().optional(),
  timeoutSecs: z.number().optional(),
  memoryMbytes: z.number().optional(),
  diskMbytes: z.number().optional(),
});

export const ApifyRunMetaSchema = z.object({
  origin: z.string().optional(),
  clientIp: z.string().optional(),
  userAgent: z.string().optional(),
});

export const ApifyRunSchema = z.object({
  id: z.string(),
  actId: z.string(),
  userId: z.string(),
  actorTaskId: z.string().nullable().optional(),
  startedAt: z.string(),
  finishedAt: z.string().nullable().optional(),
  status: ApifyRunStatusSchema,
  statusMessage: z.string().nullable().optional(),
  isStatusMessageTerminal: z.boolean().optional(),
  meta: ApifyRunMetaSchema.optional(),
  stats: ApifyRunStatsSchema,
  options: ApifyRunOptionsSchema.optional(),
  buildId: z.string(),
  buildNumber: z.string(),
  exitCode: z.number().nullable().optional(),
  defaultKeyValueStoreId: z.string(),
  defaultDatasetId: z.string(),
  defaultRequestQueueId: z.string(),
  containerUrl: z.string().optional(),
  usageUsd: z.number().optional(),
  usageTotalUsd: z.number().optional(),
});

export type ApifyRunStatus = z.infer<typeof ApifyRunStatusSchema>;
export type ApifyRunStats = z.infer<typeof ApifyRunStatsSchema>;
export type ApifyRun = z.infer<typeof ApifyRunSchema>;

// ============================================================================
// Admin Apify - List runs
// ============================================================================

export const AdminApifyRunsQuerySchema = z.object({
  limit: z.number().min(1).max(200).optional(),
  page: z.number().min(0).optional(),
  desc: z.boolean().optional(),
  status: z.union([ApifyRunStatusSchema, z.literal('all')]).optional(),
});

export const AdminApifyRunsStatsSchema = z.object({
  totalRuns: z.number(),
  successfulRuns: z.number(),
  failedRuns: z.number(),
  runningRuns: z.number(),
  totalCostUsd: z.number(),
  totalComputeUnits: z.number(),
  avgDurationMinutes: z.number(),
});

export const AdminApifyRunsPaginationSchema = PaginationOffsetSchema;

export const AdminApifyRunsResponseSchema = z.object({
  runs: z.array(ApifyRunSchema),
  stats: AdminApifyRunsStatsSchema,
  pagination: AdminApifyRunsPaginationSchema,
});

export type AdminApifyRunsQuery = z.infer<typeof AdminApifyRunsQuerySchema>;
export type AdminApifyRunsStats = z.infer<typeof AdminApifyRunsStatsSchema>;
export type AdminApifyRunsPagination = z.infer<typeof AdminApifyRunsPaginationSchema>;
export type AdminApifyRunsResponse = z.infer<typeof AdminApifyRunsResponseSchema>;

// ============================================================================
// Admin Apify - Start run
// ============================================================================

export const AdminApifyStartRunBodySchema = z.object({
  input: z.record(z.string(), z.unknown()).optional(),
  timeoutSecs: z.number().optional(),
  memoryMbytes: z.number().optional(),
  waitForFinish: z.boolean().optional(),
});

export const AdminApifyStartRunResponseSchema = z.object({
  run: ApifyRunSchema,
});

export type AdminApifyStartRunBody = z.infer<typeof AdminApifyStartRunBodySchema>;
export type AdminApifyStartRunResponse = z.infer<typeof AdminApifyStartRunResponseSchema>;

// ============================================================================
// Admin Apify - Get run details
// ============================================================================

export const AdminApifyRunParamsSchema = z.object({
  runId: z.string(),
});

export const AdminApifyRunResponseSchema = z.object({
  run: ApifyRunSchema,
});

export type AdminApifyRunParams = z.infer<typeof AdminApifyRunParamsSchema>;
export type AdminApifyRunResponse = z.infer<typeof AdminApifyRunResponseSchema>;

// ============================================================================
// Admin Apify - Delete run
// ============================================================================

export const AdminApifyDeleteRunResponseSchema = z.object({
  success: z.boolean(),
});

export type AdminApifyDeleteRunResponse = z.infer<typeof AdminApifyDeleteRunResponseSchema>;

// ============================================================================
// Admin Apify - Abort run
// ============================================================================

export const AdminApifyAbortRunResponseSchema = z.object({
  run: ApifyRunSchema,
});

export type AdminApifyAbortRunResponse = z.infer<typeof AdminApifyAbortRunResponseSchema>;
